/**
 * MarkdownEditorContainer Component
 *
 * Main container for the offer markdown editor with live preview and AI assistance.
 */
import { useCallback, useRef, useEffect, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { Save, FileDown, Eye, Send, AlertCircle, CheckCircle } from 'lucide-react';
import MarkdownEditor from './MarkdownEditor';
import PreviewPane from './PreviewPane';
import EditorToolbar from './EditorToolbar';
import AIAssistancePanel from './AIAssistancePanel';
import { useEditorStore } from '../../stores/editorStore';
import { useAI } from '../../hooks/useAI';
import { OfferContext } from '../../types/editor';

interface MarkdownEditorContainerProps {
  initialContent?: string;
  offertaId?: string;
  offerContext?: OfferContext;
  items?: Array<{
    descrizione: string;
    quantita: number;
    prezzo_unitario: number;
    sconto_percentuale?: number;
    importo_totale: number;
  }>;
  onSave?: (content: string) => Promise<void>;
  onExportPDF?: () => Promise<void>;
  onPreviewPDF?: () => Promise<void>;
  onSendForSignature?: () => Promise<void>;
  readOnly?: boolean;
  className?: string;
}

export default function MarkdownEditorContainer({
  initialContent = '',
  offertaId,
  offerContext,
  items = [],
  onSave,
  onExportPDF,
  onPreviewPDF,
  onSendForSignature,
  readOnly = false,
  className = '',
}: MarkdownEditorContainerProps) {
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Use selectors to prevent unnecessary re-renders
  const content = useEditorStore((state) => state.content);
  const setContent = useEditorStore((state) => state.setContent);
  const setOriginalContent = useEditorStore((state) => state.setOriginalContent);
  const setOfferContext = useEditorStore((state) => state.setOfferContext);
  const previewMode = useEditorStore((state) => state.previewMode);
  const isDirty = useEditorStore((state) => state.isDirty);
  const canUndo = useEditorStore((state) => state.canUndo);
  const canRedo = useEditorStore((state) => state.canRedo);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const markAsSaved = useEditorStore((state) => state.markAsSaved);
  const pushHistory = useEditorStore((state) => state.pushHistory);

  const { isAIEnabled, isLoading: isAILoading } = useAI({ offertaId });

  // Initialize content and context
  useEffect(() => {
    if (initialContent) {
      setOriginalContent(initialContent);
    }
    if (offerContext) {
      setOfferContext(offerContext);
    }
  }, [initialContent, offerContext, setOriginalContent, setOfferContext]);

  // Auto-save handler
  const handleSave = useCallback(async () => {
    if (!onSave || !isDirty) return;

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      await onSave(content);
      markAsSaved();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, content, isDirty, markAsSaved]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, undo, redo]);

  // Insert text at cursor position
  const handleInsertText = useCallback((text: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    pushHistory();
    const pos = view.state.selection.main.head;
    view.dispatch({
      changes: { from: pos, to: pos, insert: text },
      selection: { anchor: pos + text.length },
    });
    view.focus();
  }, [pushHistory]);

  // Wrap selection with text
  const handleWrapSelection = useCallback((before: string, after: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    pushHistory();
    const selection = view.state.selection.main;
    const selectedText = view.state.sliceDoc(selection.from, selection.to);

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: before + selectedText + after,
      },
      selection: {
        anchor: selection.from + before.length,
        head: selection.from + before.length + selectedText.length,
      },
    });
    view.focus();
  }, [pushHistory]);

  // Insert content from AI
  const handleInsertContent = useCallback((text: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    pushHistory();
    const pos = view.state.selection.main.head;
    // Add newlines for better formatting
    const formattedText = `\n\n${text}\n`;
    view.dispatch({
      changes: { from: pos, to: pos, insert: formattedText },
      selection: { anchor: pos + formattedText.length },
    });
    view.focus();
  }, [pushHistory]);

  // Replace selection with AI content
  const handleReplaceSelection = useCallback((text: string) => {
    const view = editorRef.current?.view;
    if (!view) return;

    pushHistory();
    const selection = view.state.selection.main;
    view.dispatch({
      changes: { from: selection.from, to: selection.to, insert: text },
      selection: { anchor: selection.from + text.length },
    });
    view.focus();
  }, [pushHistory]);

  // Handle AI toolbar actions
  const handleAIAction = useCallback((action: string, sectionType?: string) => {
    // This is handled by the toolbar calling the AI functions directly
    // The actual implementation is in AIAssistancePanel
    console.log('AI Action:', action, sectionType);
  }, []);

  // Get context for AI
  const getAIContext = useCallback(() => {
    return {
      cliente: offerContext?.cliente,
      offerta: offerContext?.offerta,
      tenant: offerContext?.tenant,
      items,
      currentContent: content,
    };
  }, [offerContext, items, content]);

  return (
    <div className={`flex flex-col h-full bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Toolbar */}
      <EditorToolbar
        onInsertText={handleInsertText}
        onWrapSelection={handleWrapSelection}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo()}
        canRedo={canRedo()}
        onAIAction={handleAIAction}
        isAIEnabled={isAIEnabled}
        isAILoading={isAILoading}
      />

      {/* Editor and Preview */}
      <div className="flex-1 overflow-hidden">
        {previewMode === 'split' ? (
          <PanelGroup direction="horizontal">
            <Panel defaultSize={50} minSize={30}>
              <MarkdownEditor
                ref={editorRef}
                value={content}
                onChange={setContent}
                readOnly={readOnly}
                autoFocus
                minHeight="100%"
                maxHeight="100%"
                className="h-full"
              />
            </Panel>
            <PanelResizeHandle className="w-1 bg-gray-200 hover:bg-blue-400 transition-colors cursor-col-resize" />
            <Panel defaultSize={50} minSize={30}>
              <PreviewPane
                content={content}
                context={offerContext}
                items={items}
                className="h-full"
              />
            </Panel>
          </PanelGroup>
        ) : previewMode === 'preview' ? (
          <PreviewPane
            content={content}
            context={offerContext}
            items={items}
            className="h-full"
          />
        ) : (
          <MarkdownEditor
            ref={editorRef}
            value={content}
            onChange={setContent}
            readOnly={readOnly}
            autoFocus
            minHeight="100%"
            maxHeight="100%"
            className="h-full"
          />
        )}
      </div>

      {/* AI Assistance Panel */}
      {isAIEnabled && (
        <AIAssistancePanel
          offertaId={offertaId}
          context={getAIContext()}
          onInsertContent={handleInsertContent}
          onReplaceSelection={handleReplaceSelection}
        />
      )}

      {/* Bottom Action Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-2">
          {/* Save Status */}
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Salvataggio...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Salvato
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Errore nel salvataggio
            </span>
          )}
          {saveStatus === 'idle' && isDirty && (
            <span className="text-sm text-amber-600">
              Modifiche non salvate
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Save Button */}
          {onSave && (
            <button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              Salva
            </button>
          )}

          {/* Preview PDF Button */}
          {onPreviewPDF && (
            <button
              onClick={onPreviewPDF}
              disabled={isDirty}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDirty ? 'Salva prima di visualizzare' : 'Anteprima PDF'}
            >
              <Eye className="h-4 w-4" />
              Anteprima
            </button>
          )}

          {/* Export PDF Button */}
          {onExportPDF && (
            <button
              onClick={onExportPDF}
              disabled={isDirty}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDirty ? 'Salva prima di esportare' : 'Esporta PDF'}
            >
              <FileDown className="h-4 w-4" />
              Esporta PDF
            </button>
          )}

          {/* Send for Signature Button */}
          {onSendForSignature && (
            <button
              onClick={onSendForSignature}
              disabled={isDirty}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDirty ? 'Salva prima di inviare' : 'Invia per firma'}
            >
              <Send className="h-4 w-4" />
              Invia per firma
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
